export const faqSections = [
  {
    id: "accounts",
    title: "Accounts and profiles",
    description: "Signing in, verification and profile details.",
    items: [
      {
        title: "Can I browse events without creating an account?",
        body: "Yes. Anyone can browse public events, seasonal hubs and creator profiles. Sign in is required when you book, manage tickets, create an event or use organiser tools.",
      },
      {
        title: "Which sign-in methods are supported?",
        body: "Namaste Network supports the sign-in methods displayed on the login screen. Google works on supported web and mobile environments. Apple sign-in is available in supported iOS production builds; it is not available inside Expo Go.",
      },
      {
        title: "Why must organisers verify their email and mobile number?",
        body: "Admin needs a reliable contact before reviewing an event or enabling Namaste ticketing. Email verification is completed with a code. A mobile contact must also be saved in Profile.",
      },
    ],
  },
  {
    id: "events",
    title: "Events and discovery",
    description: "Finding listings, festivals and recommendations.",
    items: [
      {
        title: "How do I find events near me?",
        body: "Choose a city in the top navigation, use Search, or open a seasonal hub such as Navratri or Bollywood. Category, language and campaign settings determine where an event appears.",
      },
      {
        title: "What are seasonal and cultural hubs?",
        body: "Hubs group related events into focused pages. Admin can publish several campaigns, set their route and display order, and enable or disable them without deleting the underlying events.",
      },
      {
        title: "What does a featured creator mean?",
        body: "A featured creator is an approved public profile that recommends or collaborates on an event. An event can include more than one creator, and each profile links to their public social channels.",
      },
    ],
  },
  {
    id: "tickets",
    title: "Tickets and checkout",
    description: "Ticket modes, multi-day events and payments.",
    items: [
      {
        title: "Why do different events have different ways to buy tickets?",
        body: "An event can use Namaste ticketing, an external organiser ticket link, or direct inquiry. The event page clearly shows the active method. Namaste ticketing is enabled only after admin approval.",
      },
      {
        title: "How do multi-day tickets work?",
        body: "A day ticket is tied to the date selected at checkout. Weekend and full-festival passes cover the dates stated on that pass. Your checkout summary and QR ticket show the applicable ticket name and valid day or date range.",
      },
      {
        title: "Can I buy tickets for more than one day?",
        body: "Yes, when the organiser has configured day-specific inventory. Select a date, add that day's tickets, then choose another date. Checkout shows each selected date as a separate row.",
      },
      {
        title: "Where is my QR ticket?",
        body: "Open My Bookings after payment. Each Namaste ticket has its own QR code. Wallet buttons are shown only where the event and device support that wallet format.",
      },
      {
        title: "How is the booking fee calculated?",
        body: "Checkout shows the ticket subtotal, the fee configured for that event and the final amount before you continue to Stripe. The standard Namaste fee is 5% plus 50p per order, but an event may have a different admin-approved rate.",
      },
      {
        title: "How do free RSVP and child tickets work?",
        body: "An order containing only £0 tickets is confirmed without Stripe or a booking fee, and each attendee receives an individual QR ticket. If an order contains paid adult tickets and free child tickets, Stripe charges only the paid ticket subtotal plus the displayed booking fee; QR tickets are still created for every adult and child.",
      },
      {
        title: "Who handles refunds?",
        body: "For Namaste ticketing, contact the organiser using the event details or your booking information. External ticket providers and inquiry-only events follow the organiser's own refund process.",
      },
    ],
  },
  {
    id: "entry",
    title: "Entry and support",
    description: "Using tickets at the venue and getting help.",
    items: [
      {
        title: "What should I show at the door?",
        body: "Show the QR code for the correct ticket and valid date. Increase your screen brightness and avoid sharing the code. A successful scan marks that ticket as checked in.",
      },
      {
        title: "Why is there no Namaste QR code for my event?",
        body: "External-link and inquiry-only events do not generate Namaste QR tickets. Use the confirmation supplied by the organiser or external ticketing provider.",
      },
      {
        title: "How do I contact an organiser?",
        body: "Inquiry events show the contact methods supplied by the organiser, such as WhatsApp, phone or email. For a paid Namaste booking, use the event details and retain your booking reference.",
      },
    ],
  },
];

export const organiserGuideSections = [
  {
    id: "readiness",
    title: "1. Prepare your organiser account",
    summary: "Complete these details before submitting an event.",
    route: "/profile",
    routeLabel: "Open Profile",
    steps: [
      "Verify your email using the code sent from Namaste Network.",
      "Add a working mobile number and save your profile.",
      "Add a display name, WhatsApp preference and social links where relevant.",
      "If you want direct Stripe payouts, start Stripe Express onboarding from the organiser finance section.",
    ],
  },
  {
    id: "create",
    title: "2. Create and submit an event",
    summary: "Provide enough information for customers and admin review.",
    route: "/create-event",
    routeLabel: "Create Event",
    steps: [
      "Add the title, category, language, location, venue, start and end dates, description and poster.",
      "Choose a festival or campaign only when the event belongs in that hub.",
      "Add creators who recommend or collaborate on the event.",
      "Review contact details and submit. The event stays pending until admin approves it.",
      "If admin requests changes, open My Events, amend the event and resubmit it.",
    ],
  },
  {
    id: "ticketing",
    title: "3. Choose the ticketing method",
    summary: "Use the method that matches how the event will sell.",
    steps: [
      "Namaste ticketing: customers pay in Namaste Network and receive Namaste QR tickets. Admin must approve checkout.",
      "External ticket link: send customers to Eventbrite, Fatsoma, your website or another secure checkout.",
      "Inquiry only: display WhatsApp, phone and email so customers contact you directly.",
      "A published event can request a mode change. The existing mode remains live until admin approves the request.",
      "Events with active Namaste bookings cannot remove ticketing until existing tickets are safely resolved.",
    ],
  },
  {
    id: "inventory",
    title: "4. Configure tickets and event days",
    summary: "Make every ticket's price and valid date unambiguous.",
    steps: [
      "Use day tickets for admission on one selected date. You can set a different price for each day.",
      "Use weekend passes for a defined weekend and clearly state the included dates.",
      "Use a full-festival pass when one ticket covers the full event range; customers do not select individual days for that pass.",
      "Set a tier price to £0 for free RSVP, infant or child admission. Free-only orders skip Stripe; mixed paid and free orders charge only the paid tiers while issuing a QR ticket for every attendee.",
      "Check names, prices, capacity and sale windows before approval. The checkout and QR ticket use this data.",
    ],
  },
  {
    id: "payments",
    title: "5. Set up payments and payouts",
    summary: "Choose direct payouts or Namaste-managed settlement.",
    route: "/profile",
    routeLabel: "Open Finance Setup",
    steps: [
      "With Stripe Express connected, eligible ticket payments can be routed to your Stripe account after the Namaste platform fee.",
      "Without a connected Stripe account, Namaste can collect approved ticket sales and record the organiser amount due for manual settlement.",
      "The standard buyer booking fee is 5% plus 50p per order. Admin may set an organiser fee or a specific fee for one event, and checkout displays the applicable fee before payment.",
      "The advertised ticket subtotal remains due to the organiser; the buyer booking fee funds payment processing, QR ticketing and platform support.",
      "Use the finance summary and event Reports tab to reconcile gross sales, refunds, fees and your estimated share.",
    ],
  },
  {
    id: "operations",
    title: "6. Operate the event",
    summary: "Monitor sales and prepare venue entry.",
    route: "/my-events",
    routeLabel: "Open My Events",
    steps: [
      "Open the event Dashboard to monitor tickets sold, revenue, capacity and entry rate.",
      "Use Bookings for customer records and permitted refund or resend actions.",
      "Use Reports for totals by day and ticket type, then export CSV when required.",
      "The Door Scanner is available only for approved Namaste-ticketing events with generated QR tickets.",
      "Add venue gate names, create a 4 to 8 digit scanner PIN, and share the event code, PIN and assigned gate with door staff.",
    ],
  },
  {
    id: "promotion",
    title: "7. Promote and improve discovery",
    summary: "Use campaign, creator and advertising tools deliberately.",
    steps: [
      "Choose accurate categories, language, city and campaign settings so the event appears in the right searches and hubs.",
      "Attach approved creator profiles to show trusted recommendations on the event page.",
      "Use the Boost or Ads area when paid placements are available, and verify the destination link and campaign dates.",
      "Keep the poster, title, venue and contact details current. Request admin help for material changes after publication.",
    ],
  },
];

export const adminKnowledgeSections = [
  {
    id: "moderation",
    title: "Event review and amendments",
    summary: "Approve complete listings and return incomplete ones with an actionable note.",
    route: "/admin-panel?tab=pending",
    routeLabel: "Open Pending",
    steps: [
      "Confirm organiser name, verified email and mobile contact before approval.",
      "Check poster, dates, venue, city, category, language, campaign and customer-facing contact details.",
      "Validate the selected ticketing mode and ticket inventory before enabling Namaste checkout.",
      "Use Needs changes for fixable submissions and write a specific review note. Reject only when the listing should not proceed.",
      "Approved events remain editable from Events; preserve active ticket and booking contracts when changing them.",
    ],
  },
  {
    id: "ticketing-admin",
    title: "Ticketing mode control",
    summary: "Keep checkout, customer messaging and existing bookings aligned.",
    route: "/admin-panel?tab=events",
    routeLabel: "Open Events",
    steps: [
      "Namaste ticketing creates bookings and QR tickets; external and inquiry modes do not create Namaste entry codes.",
      "Review organiser mode-change requests in Pending. The current mode remains active until approval.",
      "Do not disable Namaste ticketing while active bookings exist unless refunds, entry and customer communication are resolved.",
      "For inquiry mode, verify at least one customer contact method. For external mode, test the destination URL.",
    ],
  },
  {
    id: "finance",
    title: "Stripe, fees and settlements",
    summary: "Reconcile connected payouts and Namaste-collected organiser balances.",
    route: "/admin-panel?tab=organizers",
    routeLabel: "Open Organisers",
    steps: [
      "Check whether Stripe charges and payouts are enabled for the organiser.",
      "Set the platform percentage and fixed order fee at organiser level; use an event override only when agreed.",
      "Connected accounts receive destination transfers after application fees. Unconnected accounts accrue an organiser amount due for manual settlement.",
      "Reconcile gross sales, refunds, Stripe processing, platform fee and organiser share before recording a payout.",
      "Keep sandbox and live Stripe credentials separate and verify the active webhook before enabling real sales.",
    ],
  },
  {
    id: "scanner",
    title: "Door scanner operations",
    summary: "Issue scanner access only where Namaste QR tickets exist.",
    steps: [
      "Scanner access requires an approved Namaste-ticketing event and at least one active generated QR ticket.",
      "The organiser defines gates and creates the event PIN. Door staff use the event code, PIN and selected gate; staff names are not required.",
      "A scan validates ticket status and valid date, prevents duplicate entry and records the gate and time.",
      "Use the organiser Check-ins and Reports tabs to investigate entry totals or rejected scans.",
    ],
  },
  {
    id: "catalogue",
    title: "Categories, campaigns and visibility",
    summary: "Control discovery without deleting event records.",
    route: "/admin-panel?tab=campaigns",
    routeLabel: "Open Campaigns",
    steps: [
      "Categories can be enabled or disabled for both web and mobile presentation.",
      "Campaigns create seasonal or cultural hubs such as Navratri and Bollywood. Multiple campaigns may be active.",
      "Set campaign label, route, display content, order and enabled state. Events join a hub through their campaign field.",
      "Use Options for homepage seasonal placement and creator showcase visibility.",
    ],
  },
  {
    id: "creators",
    title: "Creators and recommendations",
    summary: "Manage public creator profiles and event associations.",
    route: "/admin-panel?tab=creators",
    routeLabel: "Open Creators",
    steps: [
      "Review profile image, display name, category, locations, languages and public social URL.",
      "Enable public profile and creator opt-in only for profiles suitable for discovery.",
      "An event may feature multiple creators. Use the event editor selection control rather than raw JSON.",
      "The global Community voices and Featured creators surfaces can be enabled or disabled in Options.",
    ],
  },
  {
    id: "intake",
    title: "Poster intake",
    summary: "Turn extracted poster information into reviewed event drafts.",
    route: "/admin-panel?tab=poster-intake",
    routeLabel: "Open Poster Intake",
    steps: [
      "New poster files are extracted into pending drafts; extraction is not publication.",
      "Open the full poster preview and compare every extracted date, venue, title, contact and price.",
      "Correct the draft, select category, language and campaign, then approve or request changes.",
      "Keep the source poster attached so future reviewers can audit the extracted information.",
    ],
  },
  {
    id: "creator-loop",
    title: "Creator Partner Loop",
    summary: "Review tagged Reels and creator DMs before outreach or publication.",
    route: "/admin-panel?tab=creator-loop",
    routeLabel: "Open Creator Loop",
    steps: [
      "Open the original post and verify the creator, event facts and official source.",
      "Review and send the prepared outreach manually, then mark the intake as contacted.",
      "Record permission before reusing creator media or representing the creator as a partner.",
      "Create an event draft only after review; the draft still requires normal event approval.",
      "Track whether the creator shared the Namaste post, followed the account or agreed to future tags.",
    ],
  },
  {
    id: "communications",
    title: "Accounts and communications",
    summary: "Maintain trusted contacts and controlled access.",
    route: "/admin-panel?tab=users",
    routeLabel: "Open Users",
    steps: [
      "Email verification uses Brevo. Profile language and location changes should update the contact attributes used for future segmentation.",
      "Google and Apple users may browse before completing a profile, but organiser actions require verified contact readiness.",
      "Easy-access demo accounts are operational test tools and must remain restricted to the approved owner IP.",
      "Never put API keys, web keys or signing credentials in event notes, public help content or source control.",
    ],
  },
];
